<template>
  <circle
    v-if="activePath && activePath.length"
    :cx="activePath[0].x"
    :cy="activePath[0].y"
    :r="largeRadius"
    fill="var(--event-map-active-path-color)"
  />
  <circle
    v-if="activePath && activePath.length"
    :cx="activePath[activePath.length - 1].x"
    :cy="activePath[activePath.length - 1].y"
    :r="largeRadius"
    fill="var(--event-map-active-path-color)"
  />
  <circle
    v-for="(point, index) in activePath"
    :key="index"
    :cx="point.x"
    :cy="point.y"
    :r="smallRadius"
    fill="var(--event-map-active-path-color)"
    :opacity="0.2"
  />
  <polyline
    v-if="activePath && activePath.length > 1"
    :key="`path_${activePath.length}_${activePath.map(p => `${p.x}-${p.y}`).join('_')}`"
    :points="activePath.map((node) => `${node.x},${node.y}`).join(' ')"
    stroke="var(--event-map-active-path-color)"
    :stroke-width="activeStrokeWidth"
    stroke-linejoin="round"
    stroke-linecap="round"
    fill="transparent"
    :style="{
      strokeDasharray: pathLength,
      strokeDashoffset: pathLength,
      animation: `activePath 3s linear forwards`
    }"
  />
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  activePath: {
    type: Array,
    default: () => [],
  },
  zoomFactor: {
    type: Number,
    default: 0,
  },
});

const pathLength = computed(() => {
  if (!props.activePath || props.activePath.length < 2) return 0;

  let length = 0;
  for (let i = 1; i < props.activePath.length; i++) {
    const dx = props.activePath[i].x - props.activePath[i - 1].x;
    const dy = props.activePath[i].y - props.activePath[i - 1].y;
    length += Math.sqrt(dx * dx + dy * dy);
  }
  return length;
});

const activeStrokeWidth = computed(() => {
  // Base width when zoom is 1. Adjust multiplier for thicker strokes.
  const base = 400;
  const factor = props.zoomFactor > 0 ? props.zoomFactor : 1;
  return Math.min(Math.max(base / factor, 50), 1000); // clamp between 50 and 1000
});

const largeRadius = computed(() => {
  const base = 840;
  const factor = props.zoomFactor > 0 ? props.zoomFactor : 1;
  const scaled = base / factor;
  return Math.min(Math.max(scaled, 84), 1000); // Adjust min/max as needed
});

const smallRadius = computed(() => {
  const base = 360;
  const factor = props.zoomFactor > 0 ? props.zoomFactor : 1;
  const scaled = base / factor;
  return Math.min(Math.max(scaled, 36), 1000);
});

</script>

<style>
@keyframes activePath {
  to {
    stroke-dashoffset: 0;
  }
}
</style>
